import { PlusOutlined } from '@ant-design/icons';
import { ProductSelect } from '@repo/crm-atoms';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row, Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type DealUpdateNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import React, { useCallback, useEffect, useState } from 'react';
import { ZodError } from 'zod';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ComparatorRenderer from '../ComparatorRenderer';
import ShowConditionsUi from '../ShowConditionsUi';
import styles from './DealUpdateMenu.module.scss';
import StatusTransition from './StatusTransition';

type Criteria = DealUpdateNodeType['data']['additionalOptions']['updateDeal']['criterias'][number];

const DEFAULT_CRITERIA: Criteria = { fromValue: 'any', toValue: 'any' };

export default function DealUpdateMenu({
  node,
}: {
  node: NodeProps<DealUpdateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const [transitions, setTransitions] = useState<Criteria[]>(
    node.data.additionalOptions?.updateDeal?.criterias ?? [],
  );

  const [productUuids, setProductUuids] = useState<string[]>(
    node.data.additionalOptions?.updateDeal?.product?.criterias?.map((c) => c.value) ?? [],
  );

  const [additionalConditionsId, setAdditionalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.criteriaGroupId ?? undefined,
  );

  const [removalConditionsId, setRemovalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.removableCriteriaGroupId ?? undefined,
  );

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      // this will throw an error if the parsing fails
      const parsedNodeData = nodeSchemas.DealUpdateNodeData.parse({
        ...node.data,
        additionalOptions: {
          updateDeal: {
            criterias: transitions,
            comparator: 'or',
            ...(productUuids.length
              ? { product: { comparator: 'or' as const, criterias: productUuids.map((value) => ({ value })) } }
              : {}),
          },
          criteriaGroupId: additionalConditionsId,
          removableCriteriaGroupId: removalConditionsId,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));
      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [additionalConditionsId, automationId, dispatch, node.data, node.id, setMenuOpen,
    removalConditionsId, transitions, contentMode, productUuids]);

  const updateExistingCriteria = useCallback((criteria: Criteria, index: number) => {
    setTransitions((prev) => {
      const next = [...prev];
      next[index] = criteria;
      return next;
    });
  }, [setTransitions]);

  const deleteCriteriaAtIndex = useCallback((index: number) => {
    setTransitions((prev) => prev.filter((_, i) => i !== index));
  }, [setTransitions]);

  const addNewTransition = useCallback(() => {
    setTransitions((prev) => [...prev, { ...DEFAULT_CRITERIA }]);
  }, [setTransitions]);

  useEffect(() => {
    if (transitions.length === 0) {
      addNewTransition();
    }
  }, [transitions.length, addNewTransition]);

  const ConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Conditions"
      btnClassName={styles.addBtn}
    />
  ), []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When a Deal's status changes`,
          [{ capitalize: true }],
        ),
      }}
    >
      <Card title={contentMode === 'edit' ? 'Edit Trigger' : 'Set Trigger'} className={styles.containerCard}>
        <div>
          <Flex gap={8} vertical>
            {transitions.map((criteria, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                <StatusTransition
                  criteria={criteria}
                  currentIndex={index}
                  updateExistingCriteria={updateExistingCriteria}
                  deleteCriteriaAtIndex={deleteCriteriaAtIndex}
                />
                {index !== transitions.length - 1 && (
                  <Row>
                    <Col span={6} />
                    <Col span={18}>
                      <ComparatorRenderer
                        comparator={node.data.additionalOptions?.updateDeal?.comparator ?? 'or'}
                        className={styles.comparator}
                      />
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            ))}
          </Flex>
          <Row>
            <Col span={6} />
            <Col span={18}>
              <Button
                className={styles.addBtn}
                onClick={addNewTransition}
                icon={<PlusOutlined />}
                type="dashed"
              >
                Add Status Transition
              </Button>
            </Col>
          </Row>
        </div>

        <Divider dashed />
        <div>
          <Row align="middle">
            <Col span={6}>
              <Typography.Text strong>Only for products</Typography.Text>
            </Col>
            <Col span={18}>
              <ProductSelect
                value={productUuids}
                onChange={setProductUuids}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
              />
            </Col>
          </Row>
        </div>

        <Divider dashed />
        <div>
          <Row align="middle">
            <Col span={6}>
              <Typography.Text strong>Set Additional Conditions</Typography.Text>
            </Col>
            <Col span={18}>
              <AutomationsCriteriaConditionsWrapper
                criteriaId={additionalConditionsId}
                onCriteriaChange={(value) => setAdditionalConditionsId(value)}
                builderUi={ConditionsBuilderUi}
              />
            </Col>
          </Row>
        </div>
        <Divider dashed />
        <div>
          <Row align="middle">
            <Col span={6}>
              <Typography.Text strong>Remove Lead(s) from this automation</Typography.Text>
            </Col>
            <Col span={18}>
              <AutomationsCriteriaConditionsWrapper
                criteriaId={removalConditionsId}
                onCriteriaChange={(value) => setRemovalConditionsId(value)}
                builderUi={ConditionsBuilderUi}
              />
            </Col>
          </Row>
        </div>
      </Card>

      <Flex justify="flex-end" gap={16}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
